import React, { useCallback, useEffect, useState } from "react";
import { api, API, fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, ArrowsClockwise, QrCode, Warning, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import EditRowSheet from "@/components/EditRowSheet";
import MovementDialog from "@/components/warehouse/MovementDialog";
import LabelDialog from "@/components/warehouse/LabelDialog";

function Field({ label, children, span = 1 }) {
  return <div style={{ gridColumn: `span ${span}` }}><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

const UNITS = ["nos", "mtr", "kg", "ltr", "set", "box", "roll", "sqft", "sqm"];
const CATEGORIES = ["general", "ductwork", "refrigerant", "electrical", "consumables", "fittings", "insulation", "tools", "spare"];

/* ============================================================
   Warehouses page — has 2 tabs: (1) Warehouses master (2) Stock inside a warehouse
============================================================ */
export default function Warehouses() {
  const [summary, setSummary] = useState(null);
  const [selectedWh, setSelectedWh] = useState(null);
  const [items, setItems] = useState([]);
  const [creatingWh, setCreatingWh] = useState(false);
  const [creatingItem, setCreatingItem] = useState(false);
  const [movementItem, setMovementItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingWh, setEditingWh] = useState(null);
  const [labelItem, setLabelItem] = useState(null);
  const emptyWh = { name: "", code: "", address: "", city: "", state: "", pincode: "", incharge: "", phone: "", active: true, notes: "" };
  const emptyItem = { warehouse_id: "", name: "", sku: "", unit: "nos", category: "general", reorder_level: 0, unit_cost: 0, notes: "" };
  const [whForm, setWhForm] = useState(emptyWh);
  const [itemForm, setItemForm] = useState(emptyItem);

  const load = useCallback(async () => {
    const s = await api.get("/warehouses/summary");
    setSummary(s.data);
    if (!selectedWh && s.data.warehouses[0]) setSelectedWh(s.data.warehouses[0].id);
  }, [selectedWh]);

  const loadItems = useCallback(async () => {
    if (!selectedWh) return setItems([]);
    const r = await api.get("/warehouse-items", { params: { warehouse_id: selectedWh } });
    setItems(r.data);
  }, [selectedWh]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const lowStockCount = items.filter((i) => Number(i.stock || 0) <= Number(i.reorder_level || 0)).length;

  const submitWh = async () => {
    try {
      await api.post("/warehouses", whForm);
      toast.success("Warehouse created");
      setCreatingWh(false); setWhForm(emptyWh); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const submitItem = async () => {
    try {
      await api.post("/warehouse-items", { ...itemForm, warehouse_id: selectedWh, reorder_level: Number(itemForm.reorder_level), unit_cost: Number(itemForm.unit_cost) });
      toast.success("Material added to warehouse");
      setCreatingItem(false); setItemForm(emptyItem); loadItems(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const deleteWh = async (id) => {
    if (!window.confirm("Delete this warehouse?")) return;
    try { await api.delete(`/warehouses/${id}`); toast.success("Deleted"); if (selectedWh === id) setSelectedWh(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this material line? All movement history will be removed.")) return;
    try { await api.delete(`/warehouse-items/${id}`); toast.success("Deleted"); loadItems(); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const createWhSheet = (
    <Sheet open={creatingWh} onOpenChange={setCreatingWh}>
      <SheetContent className="w-[520px] sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">New warehouse</SheetTitle></SheetHeader>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <Field label="Name" span={2}><Input data-testid="wh-name-input" value={whForm.name} onChange={(e) => setWhForm({ ...whForm, name: e.target.value })} placeholder="Bangalore HQ Store" /></Field>
          <Field label="Code (auto if blank)"><Input value={whForm.code} onChange={(e) => setWhForm({ ...whForm, code: e.target.value })} placeholder="WH-HQ" /></Field>
          <Field label="In-charge"><Input value={whForm.incharge} onChange={(e) => setWhForm({ ...whForm, incharge: e.target.value })} /></Field>
          <Field label="Phone"><Input value={whForm.phone} onChange={(e) => setWhForm({ ...whForm, phone: e.target.value })} /></Field>
          <Field label="City"><Input value={whForm.city} onChange={(e) => setWhForm({ ...whForm, city: e.target.value })} /></Field>
          <Field label="State"><Input value={whForm.state} onChange={(e) => setWhForm({ ...whForm, state: e.target.value })} /></Field>
          <Field label="Pincode"><Input value={whForm.pincode} onChange={(e) => setWhForm({ ...whForm, pincode: e.target.value })} /></Field>
          <Field label="Address" span={2}><Textarea rows={2} value={whForm.address} onChange={(e) => setWhForm({ ...whForm, address: e.target.value })} /></Field>
          <Field label="Notes" span={2}><Textarea rows={2} value={whForm.notes} onChange={(e) => setWhForm({ ...whForm, notes: e.target.value })} /></Field>
          <div className="col-span-2 mt-2">
            <Button onClick={submitWh} data-testid="save-warehouse-btn" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Save</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  const createItemSheet = (
    <Sheet open={creatingItem} onOpenChange={setCreatingItem}>
      <SheetContent className="w-[480px] overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">Add material to warehouse</SheetTitle></SheetHeader>
        <div className="grid grid-cols-2 gap-3 mt-6">
          <Field label="Name / Description" span={2}><Input data-testid="item-name-input" value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} placeholder="Copper Pipe 1/2 inch" /></Field>
          <Field label="SKU"><Input value={itemForm.sku} onChange={(e) => setItemForm({ ...itemForm, sku: e.target.value })} placeholder="CU-050" /></Field>
          <Field label="Unit">
            <Select value={itemForm.unit} onValueChange={(v) => setItemForm({ ...itemForm, unit: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Category">
            <Select value={itemForm.category} onValueChange={(v) => setItemForm({ ...itemForm, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Reorder level"><Input type="number" value={itemForm.reorder_level} onChange={(e) => setItemForm({ ...itemForm, reorder_level: e.target.value })} /></Field>
          <Field label="Unit cost (₹)"><Input type="number" value={itemForm.unit_cost} onChange={(e) => setItemForm({ ...itemForm, unit_cost: e.target.value })} /></Field>
          <Field label="Notes" span={2}><Textarea rows={2} value={itemForm.notes} onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })} /></Field>
          <div className="col-span-2 mt-2">
            <Button onClick={submitItem} data-testid="save-item-btn" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Save</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  const summaryCards = summary && (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="warehouse-summary">
      <SummaryTile label="Warehouses" value={summary.warehouse_count} />
      <SummaryTile label="Materials Lines" value={summary.item_count} />
      <SummaryTile label="Stock Value" value={fmtMoney(summary.total_stock_value)} tone="accent" />
      <SummaryTile label="Low-Stock Items" value={summary.low_stock_count} tone={summary.low_stock_count ? "warn" : "good"} />
    </div>
  );

  return (
    <div data-testid="warehouses-page">
      <PageHeader
        title="Warehouses"
        subtitle="Remote store / central store — track material by warehouse, stock movements, and low-stock alerts."
        module="Procurement · MM"
        ribbon={
          <>
            <BcRibbonGroup label="New">
              <BcRibbonBtn icon={Plus} label="New Warehouse" variant="primary" onClick={() => setCreatingWh(true)} testid="ribbon-new-warehouse" />
              <BcRibbonBtn icon={Plus} label="Add Material" onClick={() => setCreatingItem(true)} disabled={!selectedWh} testid="ribbon-add-material" />
            </BcRibbonGroup>
            <BcRibbonGroup label="Process">
              <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={() => { load(); loadItems(); }} testid="ribbon-refresh" />
            </BcRibbonGroup>
          </>
        }
      />
      {createWhSheet}
      {createItemSheet}

      <div className="p-6">
        {summaryCards}

        <Tabs defaultValue="stock" className="w-full">
          <TabsList className="rounded-sm bg-slate-100 p-1">
            <TabsTrigger value="stock" data-testid="tab-stock" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Warehouse Stock</TabsTrigger>
            <TabsTrigger value="master" data-testid="tab-master" className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-[#EA580C]">Warehouse Master</TabsTrigger>
          </TabsList>

          <TabsContent value="stock" className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="label-caps">Warehouse:</Label>
                <Select value={selectedWh || "__none__"} onValueChange={(v) => setSelectedWh(v === "__none__" ? null : v)}>
                  <SelectTrigger className="w-64" data-testid="warehouse-selector"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {(summary?.warehouses || []).map((w) => (
                      <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {lowStockCount > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm bg-red-50 text-red-700 flex items-center gap-1">
                    <Warning size={11} weight="fill" /> {lowStockCount} low-stock
                  </span>
                )}
              </div>
            </div>

            {(() => {
              if (!selectedWh) return (
                <EmptyState title="Select a warehouse" description="Choose a warehouse above to view its stock, or create your first warehouse from the top-right button." />
              );
              if (items.length === 0) return (
                <EmptyState title="No material lines yet" description="Add copper pipes, refrigerant, insulation, fittings — anything you keep at this location." />
              );
              return (
              <div className="bg-white border border-slate-200 rounded-sm" data-testid="warehouse-stock-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="label-caps">SKU</TableHead>
                      <TableHead className="label-caps">Material</TableHead>
                      <TableHead className="label-caps">Category</TableHead>
                      <TableHead className="label-caps text-right">Stock</TableHead>
                      <TableHead className="label-caps text-right">Reorder</TableHead>
                      <TableHead className="label-caps text-right">Unit Cost</TableHead>
                      <TableHead className="label-caps text-right">Value</TableHead>
                      <TableHead className="w-32" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((it) => {
                      const low = Number(it.stock || 0) <= Number(it.reorder_level || 0);
                      return (
                        <TableRow key={it.id} data-testid={`item-row-${it.id}`}>
                          <TableCell className="font-mono-num text-xs">{it.sku || "—"}</TableCell>
                          <TableCell className="font-medium">
                            {it.name}
                            {low && <span className="ml-2 text-[9px] font-bold uppercase tracking-widest text-red-700 bg-red-50 px-1 rounded-sm">LOW</span>}
                          </TableCell>
                          <TableCell className="text-slate-600 text-xs uppercase tracking-wider">{it.category}</TableCell>
                          <TableCell className={`text-right font-mono-num ${low ? "text-red-700 font-semibold" : ""}`}>{Number(it.stock).toFixed(2)} {it.unit}</TableCell>
                          <TableCell className="text-right font-mono-num text-slate-500 text-xs">{Number(it.reorder_level).toFixed(2)}</TableCell>
                          <TableCell className="text-right font-mono-num text-xs">{fmtMoney(it.unit_cost)}</TableCell>
                          <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(Number(it.stock) * Number(it.unit_cost))}</TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              <button onClick={() => setMovementItem(it)} data-testid={`movement-btn-${it.id}`} className="text-slate-500 hover:text-[#EA580C] p-1" title="Add movement"><ArrowsClockwise size={14} /></button>
                              <button onClick={() => setLabelItem(it)} data-testid={`label-btn-${it.id}`} className="text-slate-500 hover:text-[#EA580C] p-1" title="QR Label"><QrCode size={14} /></button>
                              <button onClick={() => setEditingItem(it)} data-testid={`edit-item-${it.id}`} className="text-slate-500 hover:text-[#EA580C] p-1" title="Edit"><PencilSimple size={14} /></button>
                              <button onClick={() => deleteItem(it.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={13} /></button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="master" className="mt-4">
            {(summary?.warehouses || []).length === 0 ? (
              <EmptyState title="No warehouses defined" description="Create your first warehouse to start tracking materials." />
            ) : (
              <div className="bg-white border border-slate-200 rounded-sm">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="label-caps">Code</TableHead>
                      <TableHead className="label-caps">Name</TableHead>
                      <TableHead className="label-caps">City</TableHead>
                      <TableHead className="label-caps">In-charge</TableHead>
                      <TableHead className="label-caps text-right">Items</TableHead>
                      <TableHead className="label-caps text-right">Stock Value</TableHead>
                      <TableHead className="label-caps text-right">Low-Stock</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(summary?.warehouses || []).map((w) => (
                      <TableRow key={w.id} data-testid={`wh-row-${w.id}`}>
                        <TableCell className="font-mono-num text-[#EA580C] font-semibold">{w.code}</TableCell>
                        <TableCell className="font-medium">{w.name}</TableCell>
                        <TableCell className="text-slate-600">{w.city || "—"}, {w.state || ""}</TableCell>
                        <TableCell className="text-slate-600 text-xs">{w.incharge || "—"}</TableCell>
                        <TableCell className="text-right font-mono-num">{w.stats?.item_count || 0}</TableCell>
                        <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(w.stats?.stock_value || 0)}</TableCell>
                        <TableCell className="text-right"><span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${(w.stats?.low_stock || 0) > 0 ? "bg-red-50 text-red-700 font-semibold" : "bg-slate-100 text-slate-500"}`}>{w.stats?.low_stock || 0}</span></TableCell>
                        <TableCell>
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => setEditingWh(w)} data-testid={`edit-wh-${w.id}`} className="text-slate-500 hover:text-[#EA580C] p-1" title="Edit"><PencilSimple size={13} /></button>
                            <button onClick={() => deleteWh(w.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={13} /></button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <MovementDialog item={movementItem} onClose={() => setMovementItem(null)} onReload={() => { loadItems(); load(); }} />

      {/* Edit warehouse */}
      {editingWh && (
        <EditRowSheet
          open={!!editingWh}
          onOpenChange={(v) => !v && setEditingWh(null)}
          title="Edit Warehouse"
          endpoint={`/warehouses/${editingWh.id}`}
          initial={editingWh}
          fields={[
            { name: "code", label: "Code", required: true },
            { name: "name", label: "Name", required: true },
            { name: "city", label: "City" },
            { name: "state", label: "State" },
            { name: "address", label: "Address", type: "textarea", colSpan: 2 },
            { name: "incharge", label: "In-charge" },
            { name: "phone", label: "Phone" },
          ]}
          onSaved={load}
        />
      )}

      {/* Edit warehouse item */}
      {editingItem && (
        <EditRowSheet
          open={!!editingItem}
          onOpenChange={(v) => !v && setEditingItem(null)}
          title="Edit Material"
          endpoint={`/warehouse-items/${editingItem.id}`}
          initial={editingItem}
          fields={[
            { name: "sku", label: "SKU" },
            { name: "name", label: "Material", required: true },
            {
              name: "category", label: "Category", type: "select",
              options: CATEGORIES.map((c) => ({ value: c, label: c })),
            },
            {
              name: "unit", label: "Unit", type: "select",
              options: UNITS.map((u) => ({ value: u, label: u })),
            },
            { name: "reorder_level", label: "Reorder Level", type: "number" },
            { name: "unit_cost", label: "Unit Cost (₹)", type: "number" },
            { name: "notes", label: "Notes", type: "textarea", colSpan: 2 },
          ]}
          transformPayload={(f) => ({
            ...f,
            reorder_level: Number(f.reorder_level || 0),
            unit_cost: Number(f.unit_cost || 0),
          })}
          onSaved={() => { loadItems(); load(); }}
        />
      )}
      <LabelDialog item={labelItem} onClose={() => setLabelItem(null)} warehouses={summary?.warehouses || []} />
    </div>
  );
}

function SummaryTile({ label, value, tone = "default" }) {
  const tones = {
    default: "bg-white border-slate-200",
    good: "bg-white border-emerald-200",
    warn: "bg-white border-amber-200",
    accent: "bg-[#0F172A] text-white border-[#0F172A]",
  };
  const valueColor = (() => {
    if (tone === "accent") return "text-[#EA580C]";
    if (tone === "warn") return "text-amber-700";
    return "text-slate-900";
  })();
  const labelColor = tone === "accent" ? "text-slate-300" : "text-slate-500";
  return (
    <div className={`border p-4 rounded-sm ${tones[tone]}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-[0.16em] ${labelColor}`}>{label}</div>
      <div className={`text-[26px] font-bold mt-2 font-mono-num ${valueColor}`}>{value}</div>
    </div>
  );
}

